import React, { useCallback, useEffect, useRef, useState } from "react";
import * as PropTypes from "prop-types";

import PaperclipSVG from "assets/svg/PaperclipSVG";
import { formatDate } from "helpers/format-date";
import { formatFileSize } from "./format-file-size";
import {
  COLLAPSED_CLASS,
  QUOTE_ATTRIBUTE,
  markQuotedNodes,
  splitQuotedText,
} from "./quoted-content";

// Links in real support email are plentiful; without a base target they would
// navigate the frame itself and replace the email body in place.
// The doctype has to lead: without it the frame parses in quirks mode, where
// body.scrollHeight never reports less than the viewport and so can only ever
// grow — a collapsed quote would leave the frame at its expanded height.
const FRAME_HEAD = `<!DOCTYPE html>
  <base target="_blank" />
  <style>
    /* Sizing the frame to its content is what removes the vertical scrollbar.
       Sideways is left scrollable: a width="600" table with nowrap cells is not
       something max-width can shrink, and clipping it puts the email out of
       reach altogether. */
    html { overflow-x: auto; overflow-y: hidden; }
    /* An email that sizes itself to the frame — html, body { height: 100% } is
       common boilerplate — would otherwise grow by its own padding every time
       we measured it and wrote the measurement back as the frame height. */
    html, body { height: auto !important; min-height: 0 !important; }
    body {
      /* flow-root keeps a child margin from collapsing out of the body and
         leaving the measured height short of what is drawn. */
      display: flow-root;
      margin: 0;
      font-family: -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto,
        Helvetica, Arial, sans-serif;
      font-size: 14px;
      line-height: 1.5;
      color: #2f3941;
      word-break: break-word;
    }
    img { max-width: 100%; height: auto; }
    table { max-width: 100%; }
    a { color: #1f73b7; }
    body.${COLLAPSED_CLASS} [${QUOTE_ATTRIBUTE}] { display: none !important; }
  </style>
`;

const SHOW_LABEL = "Show collapsed content";
const HIDE_LABEL = "Hide collapsed content";

const initialsOf = (name, email) => {
  const source = (name || email || "").trim();
  if (!source) return "?";

  const words = source.split(/[\s@._-]+/).filter(Boolean);
  const letters = words.slice(0, 2).map((word) => word[0]);

  return letters.join("").toUpperCase() || "?";
};

const CollapsedContentToggle = ({ expanded, onToggle }) => (
  <button
    type="button"
    className="inboxEmailQuoteToggle"
    aria-expanded={expanded}
    aria-label={expanded ? HIDE_LABEL : SHOW_LABEL}
    title={expanded ? HIDE_LABEL : SHOW_LABEL}
    onClick={onToggle}
  >
    <span className="inboxEmailQuoteDots" aria-hidden="true" />
  </button>
);

CollapsedContentToggle.propTypes = {
  expanded: PropTypes.bool.isRequired,
  onToggle: PropTypes.func.isRequired,
};

const attachmentShape = PropTypes.shape({
  id: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  attributes: PropTypes.shape({
    name: PropTypes.string,
    content_type: PropTypes.string,
    size: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
    url: PropTypes.string,
  }),
});

const Attachment = ({ attachment }) => {
  const {
    name,
    size,
    content_type: contentType,
    url,
  } = attachment.attributes || attachment;
  const label = name || "Attachment";
  const sizeLabel = formatFileSize(size);

  return (
    <li className="inboxEmailAttachment">
      <PaperclipSVG classProp="inboxEmailAttachmentIcon" />
      {url ? (
        // The url is a signed link that expires, so it is used exactly as
        // handed over and never stored. Whether the file saves or opens is
        // S3's own disposition to decide: the download hint only carries
        // weight on a link of our own origin.
        <a
          className="inboxEmailAttachmentName"
          href={url}
          download={label}
          target="_blank"
          rel="noreferrer"
          title={contentType || undefined}
        >
          {label}
        </a>
      ) : (
        // An attachment whose link the API left out is still worth naming, so
        // the agent knows to ask for it rather than assuming the email came
        // bare.
        <span
          className="inboxEmailAttachmentName"
          title={contentType || undefined}
        >
          {label}
        </span>
      )}
      {sizeLabel && (
        <span className="inboxEmailAttachmentSize">{sizeLabel}</span>
      )}
    </li>
  );
};

Attachment.propTypes = {
  attachment: attachmentShape.isRequired,
};

const EmailMessage = ({ email, attachments = [] }) => {
  const attrs = email.attributes || email;
  const frameRef = useRef(null);
  const observerRef = useRef(null);
  const [frameHeight, setFrameHeight] = useState(0);
  const [hasQuote, setHasQuote] = useState(false);
  const [expanded, setExpanded] = useState(false);

  const {
    from_email: fromEmail,
    from_name: fromName,
    to_email: toEmail,
    html_body: htmlBody,
    text_body: textBody,
    sent_at: sentAt,
    created_at: createdAt,
  } = attrs;

  const timestamp = sentAt || createdAt;

  // Measuring the body rather than the document keeps the frame from locking
  // itself to its own height: an empty document is as tall as the frame.
  const measure = useCallback(() => {
    const frame = frameRef.current;
    const doc = frame?.contentDocument;
    const body = doc?.body;
    if (!body) return;

    // A sideways scrollbar sits under the content and would be clipped without
    // room of its own. Its height stays constant as the frame grows, so adding
    // it here cannot feed back into the next measurement.
    const root = doc.documentElement;
    const scrollsSideways = root.scrollWidth > root.clientWidth;
    const scrollbar = scrollsSideways
      ? Math.max(0, (frame.contentWindow?.innerHeight || 0) - root.clientHeight)
      : 0;

    setFrameHeight(Math.max(body.scrollHeight, body.offsetHeight) + scrollbar);
  }, []);

  // The iframe sandbox omits allow-scripts, so sender markup cannot run
  // anything; allow-same-origin is only here so we can measure the rendered
  // height and size the frame to its content. The popup grants let a clicked
  // link open in a normal tab rather than being blocked.
  const onFrameLoad = useCallback(() => {
    const doc = frameRef.current?.contentDocument;
    const body = doc?.body;
    if (!body) return;

    // A frame reloads whenever React moves its node, so this runs again on an
    // email whose quote is already open. Seeding both flags from what was just
    // found keeps the toggle from describing the opposite of what is on screen.
    const found = markQuotedNodes(doc);
    body.classList.toggle(COLLAPSED_CLASS, found);
    setHasQuote(found);
    setExpanded(false);

    measure();

    // Images and web fonts arrive after load and grow the body past the height
    // measured here, which is what left every email with its own scrollbar.
    observerRef.current?.disconnect();
    if (typeof ResizeObserver !== "undefined") {
      observerRef.current = new ResizeObserver(measure);
      observerRef.current.observe(body);
    }
  }, [measure]);

  useEffect(() => () => observerRef.current?.disconnect(), []);

  useEffect(() => {
    if (!hasQuote) return;

    const body = frameRef.current?.contentDocument?.body;
    if (!body) return;

    body.classList.toggle(COLLAPSED_CLASS, !expanded);
    measure();
  }, [expanded, hasQuote, measure]);

  const onToggle = useCallback(() => setExpanded((shown) => !shown), []);

  const { visible: visibleText, quoted: quotedText } = splitQuotedText(
    textBody || ""
  );

  return (
    <article className="inboxEmail">
      <div className="inboxEmailAvatar">{initialsOf(fromName, fromEmail)}</div>
      <div className="inboxEmailContent">
        <div className="inboxEmailHeader">
          <span className="inboxEmailSender">{fromName || fromEmail}</span>
          <span className="inboxEmailTime">
            {timestamp ? formatDate(timestamp, "yyyy-MM-dd HH:mm") : ""}
          </span>
        </div>
        <div className="inboxEmailRecipient">To: {toEmail || "—"}</div>
        {htmlBody ? (
          <>
            <iframe
              ref={frameRef}
              className="inboxEmailFrame"
              title={`Email ${email.id}`}
              sandbox="allow-same-origin allow-popups allow-popups-to-escape-sandbox"
              referrerPolicy="no-referrer"
              srcDoc={`${FRAME_HEAD}${htmlBody}`}
              onLoad={onFrameLoad}
              style={{ height: frameHeight ? `${frameHeight}px` : "auto" }}
            />
            {hasQuote && (
              <CollapsedContentToggle expanded={expanded} onToggle={onToggle} />
            )}
          </>
        ) : (
          <>
            <div className="inboxEmailTextBody">
              {visibleText || "(empty email)"}
            </div>
            {quotedText && (
              <CollapsedContentToggle expanded={expanded} onToggle={onToggle} />
            )}
            {quotedText && expanded && (
              <div className="inboxEmailTextBody inboxEmailQuotedText">
                {quotedText}
              </div>
            )}
          </>
        )}
        {!!attachments.length && (
          <ul className="inboxEmailAttachments">
            {attachments.map((attachment) => (
              <Attachment key={attachment.id} attachment={attachment} />
            ))}
          </ul>
        )}
      </div>
    </article>
  );
};

EmailMessage.propTypes = {
  email: PropTypes.shape({
    id: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
    attributes: PropTypes.shape({
      from_email: PropTypes.string,
      from_name: PropTypes.string,
      to_email: PropTypes.string,
      html_body: PropTypes.string,
      text_body: PropTypes.string,
      sent_at: PropTypes.string,
      created_at: PropTypes.string,
    }),
  }).isRequired,
  attachments: PropTypes.arrayOf(attachmentShape),
};

export default EmailMessage;
